// Quick view: fetches the product page, keeps only the blocks marked
// data-show-inside-quick-view, forces its own block order and its own gallery
// type (grid or slider) so the modal never inherits the Main product layout.
if (!customElements.get("quick-view-modal")) {
  // Blocks always render in this order inside the modal, whatever order the
  // product page uses (the design puts stock above the tabs, buttons last).
  const QUICK_VIEW_BLOCK_ORDER = [
    ".product__badges",
    ".product__vendor",
    ".product__title",
    ".product__price",
    ".product-parameters",
    ".product__inventory",
    ".product-content-tabs",
    ".product__description",
    ".product__sku",
    ".product-custom-property",
    ".product__buy-buttons",
  ];

  // Below this width both gallery types show the slider.
  const QUICK_VIEW_SLIDER_BREAKPOINT = 990;

  const QUICK_VIEW_SLIDES_PER_VIEW = 1.55;

  const QUICK_VIEW_BLOCKED_SELECTORS = [
    ".product__gallery-blocks",
    ".linked-products",
    ".product-popup",
    ".product-popup-modal",
    ".product-parameters__popup-row",
    ".product-sticky-add-bar",
    ".pickup-availability",
    ".back-in-stock-alert",
    ".back-in-stock-alert-drawer",
    "[data-back-in-stock-alert-script]",
    ".product-form__checkout",
    "product-modal",
    ".product__media-toggle",
    ".product__media-mobile-icon",
    ".product__media-sublist-wrapper",
    ".slider-pagination",
    ".custom-liquid",
    ".complementary-products",
  ];

  customElements.define(
    "quick-view-modal",
    class QuickViewModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickViewInfo-"]');
        if (!this.modalContent) return;

        this.galleryType = this.dataset.galleryType || "grid";

        this.preventScrollBody();
      }

      disconnectedCallback() {
        this.teardownGallery();
      }

      hide(preventFocus = false) {
        this.openedBy =
          this.openedBy
            ?.closest(".product-card")
            ?.querySelector(".product-card__name > a") || this.openedBy;
        if (preventFocus) this.openedBy = null;

        super.hide();

        this.addEventListener(
          "transitionend",
          () => {
            if (!this.hasAttribute("open")) {
              // Tear the gallery down before wiping its DOM, else each
              // open/close orphans a Swiper and its observer.
              this.teardownGallery();
              this.modalContent.innerHTML = "";
            }
          },
          { once: true }
        );

        document.body.dispatchEvent(new CustomEvent("quick-view-closed"));

        // styles for forced disable body scroll on iOS
        if (typeof window.unlockScrollBodyOnIos === "function") {
          window.unlockScrollBodyOnIos(this.openScrollY);
        }
      }

      show(opener) {
        if (!opener || !opener?.dataset?.productUrl) return;

        opener.setAttribute("aria-disabled", true);
        opener.classList.add("loading");
        const btnSpinner = opener.querySelector(".button-loader");
        if (btnSpinner) btnSpinner.classList.remove("hidden");

        fetch(opener.dataset.productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.productElement = responseHTML.querySelector(
              '[id^="MainProduct-"]'
            );
            if (!this.productElement) return;

            this.prepareProductHTML();
            this.setModalHTML();
            this.initSlider();
            this.initShopifyElements();
            super.show(opener);
            this.setFocusElement(opener);

            // styles for forced disable body scroll on iOS
            if (typeof window.lockScrollBodyOnIos === "function") {
              this.openScrollY = window.scrollY;
              window.lockScrollBodyOnIos(this.openScrollY);
            }
          })
          .catch(() => {
            // A dropped request must not leave an unhandled rejection behind;
            // the button is restored in finally and the shopper can retry.
          })
          .finally(() => {
            opener.removeAttribute("aria-disabled");
            opener.classList.remove("loading");
            if (btnSpinner) btnSpinner.classList.add("hidden");
          });
      }

      prepareProductHTML() {
        this.preventDuplicatedIDs();
        this.removeDOMElements(this.productElement);
      }

      stripBlockedElements(root) {
        if (!root) return;

        root
          .querySelectorAll(QUICK_VIEW_BLOCKED_SELECTORS.join(", "))
          .forEach((el) => el.remove());
      }

      removeDOMElements(mainProductElement) {
        mainProductElement
          .querySelectorAll(
            ".product__info-inner > :not([data-show-inside-quick-view='true'])"
          )
          .forEach((el) => el.remove());

        this.stripBlockedElements(mainProductElement);

        const onlyOneBlockSelectors = [
          ".product-parameters",
          ".product__vendor",
          ".product__price",
          ".product__title",
          ".product__description",
          ".product__inventory",
          ".product__buy-buttons",
        ];

        onlyOneBlockSelectors.forEach((selector) => {
          const items = mainProductElement.querySelectorAll(selector);
          if (items.length > 1) {
            items.forEach((el, index) => {
              if (index > 0) el.remove();
            });
          }
        });
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;

        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickview-${sectionId}`
          );

        this.productElement
          .querySelectorAll("variant-picker")
          .forEach((variantPicker) => {
            variantPicker.dataset.originalSection = sectionId;
          });
      }

      setModalHTML() {
        this.modalContent.innerHTML = this.productElement.innerHTML;

        this.reinjectScriptTags();
        this.createFullDetailsButton();
        this.reorderElements();
        this.preventVariantURLSwitching();
      }

      reinjectScriptTags() {
        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        this.modalContent.querySelectorAll("script").forEach((oldScriptTag) => {
          const newScriptTag = document.createElement("script");
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML)
          );
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      createFullDetailsButton() {
        const fullDetailsBtnTemplate = this.querySelector(
          "[data-full-details-button]"
        );
        if (!fullDetailsBtnTemplate) return;

        // The design stacks it under the add-to-cart row; the info column is the
        // fallback for a product with no buy-buttons block.
        const buttonsWrapper =
          this.modalContent.querySelector(".product-form__buttons") ||
          this.modalContent.querySelector(".product__info-inner");
        if (!buttonsWrapper) return;

        buttonsWrapper.appendChild(
          fullDetailsBtnTemplate.content.cloneNode(true)
        );
      }

      reorderElements() {
        const infoInner = this.modalContent.querySelector(
          ".product__info-inner"
        );
        if (!infoInner) return;

        QUICK_VIEW_BLOCK_ORDER.forEach((selector) => {
          const block = infoInner.querySelector(`:scope > ${selector}`);
          if (block) infoInner.appendChild(block);
        });
      }

      preventScrollBody() {
        // iOS Safari scrolls the page behind the fixed modal when a clipped
        // option radio receives focus (body overflow lock is ignored there).
        // Delegated focusin survives variant-change re-renders of the options;
        // called once from the constructor.
        this.modalContent.addEventListener("focusin", (event) => {
          if (
            !event.target.matches(
              '.product-form__controls-group input[type="radio"]'
            )
          ) {
            return;
          }
          const scrollTop =
            document.body.scrollTop || document.documentElement.scrollTop;
          setTimeout(() => {
            window.scrollTo(0, scrollTop);
          }, 0);
        });
      }

      preventVariantURLSwitching() {
        const variantPickerEl =
          this.modalContent.querySelectorAll("variant-picker");

        variantPickerEl.forEach((el) => {
          el.setAttribute("data-update-url", "false");
        });
      }

      // The modal owns its gallery type: every layout class and data attribute
      // the product page set is dropped, then the quick view type is applied.
      setGalleryType() {
        const mediaWrapper = this.modalContent.querySelector(".product__media");
        if (!mediaWrapper) return;

        const keepClasses = ["product__media", "product__media--no-media"];
        mediaWrapper.className = [...mediaWrapper.classList]
          .filter((cssClass) => keepClasses.includes(cssClass))
          .join(" ");

        mediaWrapper.dataset.quickViewType = this.galleryType;
        delete mediaWrapper.dataset.desktopType;
        delete mediaWrapper.dataset.mobileType;

        const mediaList = mediaWrapper.querySelector(".product__media-list");
        if (!mediaList) return;

        mediaList.dataset.quickViewType = this.galleryType;
        delete mediaList.dataset.desktopType;
        delete mediaList.dataset.mobileType;
        delete mediaList.dataset.autoplay;
      }

      promoteMedia(mediaId) {
        if (!mediaId || this.galleryType !== "grid" || this._swiper) return;

        const container = this.modalContent.querySelector(
          ".product__media-list-container"
        );
        if (!container) return;

        const item = container.querySelector(
          `.product__media-item[data-media-id="${mediaId}"]`
        );
        if (!item || item === container.firstElementChild) return;

        container.insertBefore(item, container.firstElementChild);
      }

      teardownGallery() {
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;

        this._swiper?.destroy(true, true);
        this._swiper = null;
      }

      // Also called from global.js after a variant change replaces the media,
      // so it re-applies the quick view gallery type every time.
      initSlider() {
        this.teardownGallery();
        this.setGalleryType();

        const sliderEl = this.modalContent.querySelector(
          ".product__media-list"
        );
        if (!sliderEl) return;

        const sliderWrapper = sliderEl.querySelector(
          ".product__media-list-container"
        );
        const slides = sliderEl.querySelectorAll(".product__media-item");
        const buttonPrev = sliderEl.querySelector(".slider-nav-button--prev");
        const buttonNext = sliderEl.querySelector(".slider-nav-button--next");

        if (!sliderWrapper || slides.length < 2) {
          if (buttonPrev) buttonPrev.remove();
          if (buttonNext) buttonNext.remove();
          return;
        }

        // Swiper needs its own classes: the fetched markup only carries them
        // when the product page itself renders a slider.
        sliderEl.classList.add("swiper");
        sliderWrapper.classList.add("swiper-wrapper");
        slides.forEach((slide) => slide.classList.add("swiper-slide"));

        const canLoop =
          slides.length >= Math.ceil(QUICK_VIEW_SLIDES_PER_VIEW * 2);

        const initSwiper = () => {
          if (this._swiper) return;

          this._swiper = new Swiper(sliderEl, {
            // The design centres the active slide and lets both neighbours peek.
            slidesPerView: QUICK_VIEW_SLIDES_PER_VIEW,
            centeredSlides: true,
            spaceBetween: 1,
            loop: canLoop,
            rewind: !canLoop,
            speed: 800,
            direction: "horizontal",
            allowTouchMove: true,
            watchSlidesProgress: true,
            mousewheel: {
              forceToAxis: true,
            },
            navigation: {
              nextEl: buttonNext,
              prevEl: buttonPrev,
              disabledClass: "swiper-button-disabled",
            },
            on: {
              slideChange: function () {
                window.pauseAllMedia(sliderEl);
              },
            },
          });
        };

        const destroySwiper = () => {
          if (!this._swiper) return;

          this._swiper.destroy(true, true);
          this._swiper = null;
        };

        if (this.galleryType === "slider") {
          initSwiper();
          return;
        }

        // Grid: the slider only runs below the breakpoint, where the design
        // shows the slider for both types. ResizeObserver (not a resize
        // listener) so the modal reacts to its own panel too.
        const syncSwiper = () => {
          if (window.innerWidth < QUICK_VIEW_SLIDER_BREAKPOINT) {
            initSwiper();
          } else {
            destroySwiper();
          }
        };

        syncSwiper();

        this._resizeObserver = new ResizeObserver(syncSwiper);
        this._resizeObserver.observe(this);
      }

      initShopifyElements() {
        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
      }

      setFocusElement(opener) {
        const productCard = opener.closest("product-card");
        const productCardLink = productCard?.querySelector(
          ".product-card__media-link"
        );
        if (productCardLink) this.openedBy = productCardLink;
      }
    }
  );
}
